import { CircularProgress } from '@material-ui/core';
import { SortOrder, TimeSeriesAggregation } from '@moderepo/mode-apis';
import { BaseEntityField, capitalizeAll, GenericMessageBox, roundValue } from '@moderepo/mode-app-components';
import { fetchTimeSeriesCollectionData, fetchTimeSeriesCollectionInfo, userAppDataContext, selectTimeSeriesCollectionData,
    selectTimeSeriesCollectionInfo } from '@moderepo/mode-data-state-user-app';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import clsx from 'clsx';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { MetricInfo, RCMetricsValuesDisplay, RCMetricsValuesDisplayProps } from '../../components/robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { asNumber } from '../../utils/utils';
import { getDateTimeDisplay } from '../containerUtils';


/**
 * The structure for storing the latest metrics info for 1 collection
 */
interface CollectionLatestMetricsValues {
    readonly startDate: string | undefined;
    readonly endDate: string | undefined;
    readonly numericMetrics: readonly MetricInfo[];
}



export interface RCCollectionLatestMetricDisplayContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly collectionName: string;
    readonly collectionId: string;
    readonly tsdbSmartModuleId: string;
    readonly dateRange: {
        readonly startDate: number;
        readonly endDate: number;
    }
    readonly aggregationType?: TimeSeriesAggregation | undefined;
    readonly onViewDetailsClicked?: (collectionId: string)=> void;
    readonly onDownloadDataClicked?: (collectionId: string)=> void;
}


/**
 * This component displays all the metrics for 1 Collection
 * @param props
 */
export const RCCollectionLatestMetricDisplayContainer: React.FC<RCCollectionLatestMetricDisplayContainerProps> = (
    props: RCCollectionLatestMetricDisplayContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);

    // Select the collection INFO from data store and get the valueNames from the collection info once collection info is ready
    const valueNames = useMemo((): readonly string[] | undefined => {
        const collectionInfo = selectTimeSeriesCollectionInfo(appDataState, props.homeId, props.tsdbSmartModuleId, props.collectionId);
        if (collectionInfo) {
            return collectionInfo.valueNames;
        }
        return undefined;
    }, [appDataState, props.collectionId, props.homeId, props.tsdbSmartModuleId]);


    // Select the collection DATA from data store and create CollectionLatestMetricsValues once collection data is ready
    const collectionLatestMetricsValues = useMemo((): CollectionLatestMetricsValues => {
        if (valueNames) {
            const collectionData = selectTimeSeriesCollectionData(
                appDataState, props.homeId,
                props.tsdbSmartModuleId,
                props.collectionId,
                (new Date(props.dateRange.startDate)).toISOString(),
                (new Date(props.dateRange.endDate)).toISOString(),
                valueNames, props.aggregationType,
            );

            if (collectionData && collectionData.data.length > 0) {
                return {
                    startDate     : collectionData.data[0][0] as string,       // The first element in the data will be the date of the values
                    endDate       : collectionData.data[collectionData.data.length - 1][0] as string,       // The last element
                    numericMetrics: valueNames.map((valueName: string, index: number): MetricInfo => {
                        return {
                            value: collectionData.data.reduce((sum: number, value: readonly (number | string | null)[]): number => {
                                // We can assume value[index + 1] is a number because 'valueNames' is a numeric metric
                                // But it's possible the numeric value can be null. Fallback to 0 when the value is null.
                                // FIXME
                                // It can be better to handle null as null instead of handle null as 0 for the sake of user experience.
                                // Think out the appropriate ways later.
                                return sum + asNumber(value[index + 1]);
                            }, 0),
                            name: valueName,
                        };
                    }),
                };
            }

            // No collectionData, could be collectionData is not loaded or the collection is indeed has no data
            return {
                startDate     : (new Date(props.dateRange.startDate)).toISOString(),
                endDate       : (new Date(props.dateRange.endDate)).toISOString(),
                numericMetrics: valueNames.map((valueName: string): MetricInfo => {
                    return {
                        value: 0,
                        name : valueName,
                    };
                }),
            };
        }

        // valueNames not ready, probably because collectionInfo not done loading
        return {
            startDate     : undefined,
            endDate       : undefined,
            numericMetrics: [],
        };
    }, [appDataState, props.aggregationType, props.collectionId, props.dateRange.endDate, props.dateRange.startDate, props.homeId,
        props.tsdbSmartModuleId, valueNames]);


    // This useEffect is used for fetching collection INFO when the app init
    useEffect(() => {
        if (!valueNames) {
            // Dispatch an action to fetch collection info so we can get the list of valueNames
            appDataDispatch(
                fetchTimeSeriesCollectionInfo(props.homeId, props.tsdbSmartModuleId, props.collectionId), false, true,
            ).catch(() => {
                // ignore api  error
            });
        }
    }, [valueNames, props.homeId, props.tsdbSmartModuleId, props.collectionId, appDataDispatch]);


    /**
     * This useEffect is used for re-fetching collection data when startDate or endDate changed
     */
    useEffect(() => {
        if (valueNames && valueNames.length > 0) {
            // Dispatch an action to fetch collection data after we got the list of valueNames
            appDataDispatch(fetchTimeSeriesCollectionData(
                props.homeId, props.tsdbSmartModuleId, props.collectionId,
                (new Date(props.dateRange.startDate)).toISOString(),
                (new Date(props.dateRange.endDate)).toISOString(),
                valueNames, props.aggregationType,
            ), false).catch(() => {
                // ignore api  error
            });
        }
    }, [props.dateRange, valueNames, props.homeId, props.tsdbSmartModuleId, props.collectionId, props.aggregationType, appDataDispatch]);


    /**
     * Create 1 field for each metric. This set of fields will be displayed in the RCMetricValuesDisplay component
     */
    const fieldSettings = useMemo((): RCMetricsValuesDisplayProps['fields'] | undefined => {
        if (collectionLatestMetricsValues) {

            const numericMetricFields = collectionLatestMetricsValues.numericMetrics.reduce((
                result: { [fieldName: string]: BaseEntityField<string> },
                metricInfo: MetricInfo,
            ): {
                [fieldName: string]: BaseEntityField<string>
            } => {
                return {
                    ...result,
                    [metricInfo.name]: {
                        label: metricInfo.name,
                        value: metricInfo.value !== undefined && metricInfo.value !== null
                            ? trans('label.value_unit_display', {
                                value: roundValue(Number(metricInfo.value), 3),
                            })
                            : trans('label.none'),
                    },
                };
            }, {});

            return {
                numericMetricFields,
            };
        }

        return undefined;
    }, [collectionLatestMetricsValues, trans]);


    const onViewCollectionDetailsClick = useCallback(() => {
        if (props.onViewDetailsClicked) {
            props.onViewDetailsClicked(props.collectionId);
        }
    }, [props]);


    return (
        <>
            {collectionLatestMetricsValues && fieldSettings && (
                <RCMetricsValuesDisplay
                    className={clsx(props.className, 'match-height')}
                    title={props.collectionName}
                    timestamp={collectionLatestMetricsValues.startDate
                        ? getDateTimeDisplay(collectionLatestMetricsValues.startDate, trans)
                        : trans('label.none')}
                    fields={fieldSettings}
                    fieldsOrdering={SortOrder.DESC}
                    customSecondaryActions={[{
                        label  : capitalizeAll(trans('label.view_historical_data')),
                        icon   : 'show_chart',
                        onClick: () => {
                            onViewCollectionDetailsClick();
                        },
                    }, {
                        label  : capitalizeAll(trans('label.download_time_series_data')),
                        icon   : 'get_app',
                        onClick: () => {
                            if (props.onDownloadDataClicked) {
                                props.onDownloadDataClicked(props.collectionId);
                            }
                        },
                    }]}
                    text={{
                        updatedDate: capitalizeAll(trans('label.updated_date')),
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!(collectionLatestMetricsValues && fieldSettings) && (
                <CircularProgress />
            )}
        </>
    );
};
